/* eslint-disable camelcase */
import { useCallback } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { CompanyStatus } from '../../../../common/constants';
import { VerificationMethod } from '../../../../common/interfaces/entities';
import {
  trackClickEvent,
  trackModalViewedEvent,
} from '../../../../modules/GlintsTracking/tracking';
import { getSessionCompanyCountryCode } from '../../../../selectors/session';
import { CompanyVerificationFormState } from '../types';

export const DocumentTypes = {
  NPWP: 'npwp',
  ACRA_BIZFILE: 'acra_bizfile',
  COMPANY_REGISTRATION_DOCUMENT: 'company_registration_document',
} as const;

export type DocumentTypes = (typeof DocumentTypes)[keyof typeof DocumentTypes];

export const NibTypes = {
  NPWP: 'npwp',
  UEN: 'uen',
  BRN: 'brn',
} as const;

export type NibTypes = (typeof NibTypes)[keyof typeof NibTypes];

export const PAGE_NAME = 'company_verification';
export const VERIFICATION_COMPANY_NIB_NUMBER_FILLED =
  'verification_company_nib_number_filled';

export function getVerificationMethodProperty(
  verificationMethod?: VerificationMethod
): Record<string, unknown> {
  const verificationMethodMap: Partial<Record<VerificationMethod, string>> = {
    [VerificationMethod.LEGAL_DOCUMENTS]: 'legal_document',
    [VerificationMethod.PROOF_OF_OWNERSHIP]: 'proof_of_ownership',
  };

  if (!verificationMethod) {
    return {};
  }

  return {
    verification_method: verificationMethodMap[verificationMethod],
  };
}

export function trackCompanyVerificationViewedEvent(
  companyStatus?: CompanyStatus,
  isCompanyVerificationInProgress?: boolean,
  countryCode?: string,
  verificationMethod?: VerificationMethod
) {
  let modalName = PAGE_NAME;
  if (companyStatus === CompanyStatus.VERIFIED) {
    modalName = 'company_verification_success';
  } else if (companyStatus === CompanyStatus.REJECTED) {
    modalName = 'company_verification_rejected';
  } else if (isCompanyVerificationInProgress) {
    modalName = 'company_verification_in_progress';
  }

  trackModalViewedEvent(modalName, {
    country_code: countryCode,
    ...getVerificationMethodProperty(verificationMethod),
  });
}

export function useCompanyVerificationTracking() {
  const { trigger, getFieldState, getValues } =
    useFormContext<CompanyVerificationFormState>();
  const countryCode = useSelector(getSessionCompanyCountryCode);

  const trackCompanyVerificationClickEvent = useCallback(
    (name: string, properties: Record<string, unknown> = {}) => {
      trackClickEvent({
        elementName: name,
        properties: {
          country_code: countryCode,
          ...getVerificationMethodProperty(getValues('verificationMethod')),
          ...properties,
        },
        pageName: PAGE_NAME,
        useTargetPageName: true,
      });
    },
    [countryCode, getValues]
  );

  function trackCompanyVerificationFileUploadEvent(
    fieldName: FieldPath<CompanyVerificationFormState>
  ) {
    const personalIdFieldNames = [
      'verifyWithProofOfOwnership.personalNPWPDoc',
      'verifyWithProofOfOwnership.ktpDoc',
    ];

    const elementName = personalIdFieldNames.includes(fieldName)
      ? 'verification_upload_id_document'
      : 'verification_upload_legal_document';

    const properties: Record<string, unknown> = {};
    if (elementName === 'verification_upload_legal_document') {
      const fieldDocumentTypeMap: Partial<
        Record<FieldPath<CompanyVerificationFormState>, DocumentTypes>
      > = {
        'verifyWithLegalDocuments.companyNPWPDoc': DocumentTypes.NPWP,
        'verifyWithLegalDocuments.acraBizFile': DocumentTypes.ACRA_BIZFILE,
        'verifyWithLegalDocuments.companyLegalDoc':
          DocumentTypes.COMPANY_REGISTRATION_DOCUMENT,
      };

      properties.document_type = fieldDocumentTypeMap[fieldName] ?? undefined;
    }

    trackCompanyVerificationClickEvent(elementName, properties);
  }

  function trackCompanyVerificationValidateImageEvent(
    properties: Record<string, unknown> = {}
  ) {
    const elementName = 'validate_image_quality';

    trackCompanyVerificationClickEvent(elementName, {
      ...properties,
      document_type: DocumentTypes.NPWP,
    });
  }

  function trackCompanyVerificationMultiFileUploadEvent(
    fieldName: FieldPath<CompanyVerificationFormState>,
    photoCount: number
  ) {
    if (fieldName !== 'verifyWithProofOfOwnership.businessActivityImages') {
      return;
    }

    trackCompanyVerificationClickEvent('verification_upload_activity_photo', {
      photo_count: photoCount,
    });
  }

  function trackCompanyVerificationMultiFileUploadListEvent(
    fieldName: FieldPath<CompanyVerificationFormState>
  ) {
    const fieldElementNameMap: Partial<
      Record<FieldPath<CompanyVerificationFormState>, string>
    > = {
      'verifyWithLegalDocuments.additionalDocuments':
        'verification_additional_document_upload',
      'verifyWithProofOfOwnership.additionalDocuments':
        'verification_additional_personal_document_upload',
    };

    const elementName = fieldElementNameMap[fieldName];

    if (!elementName) {
      return;
    }

    trackCompanyVerificationClickEvent(elementName);
  }

  const trackOnFieldValid = useCallback(
    (
      fieldName: FieldPath<CompanyVerificationFormState>,
      elementName: string,
      properties: Record<string, unknown> = {}
    ) =>
      async () => {
        await trigger(fieldName);
        if (
          (getValues(fieldName) as string)?.trim() &&
          !getFieldState(fieldName).invalid
        ) {
          trackCompanyVerificationClickEvent(elementName, properties);
        }
      },
    [trigger, getFieldState, getValues, trackCompanyVerificationClickEvent]
  );

  return {
    trackCompanyVerificationClickEvent,
    trackCompanyVerificationFileUploadEvent,
    trackCompanyVerificationMultiFileUploadEvent,
    trackCompanyVerificationMultiFileUploadListEvent,
    trackOnFieldValid,
    trackCompanyVerificationValidateImageEvent,
  };
}
